import { authedFetch } from './api.ts';

/**
 * Saving a file the browser cannot simply be pointed at.
 *
 * Every download here is a `fetch`, not an `<a href>`, because the request
 * needs an Authorization header and a plain link cannot carry one — putting
 * the token in the query string would leak it into server logs and browser
 * history. Which is correct and has one consequence: the browser's own
 * download indicator never appears, because as far as it knows nothing is
 * being downloaded. Nothing moved, no percentage, no shelf at the bottom of
 * the window.
 *
 * A 7MB archive took long enough that the client pressed Download, saw
 * nothing happen, and concluded the button was broken. It was not; it had
 * three more minutes to go and no way to say so.
 *
 * So the body is read a chunk at a time and reported against the
 * Content-Length the server already sends. That is the whole fix: the bytes
 * arrive at exactly the speed they did before, and now you can watch them.
 */

export interface Progress {
  /** 0–1 where the total is known, null where it is not. */
  (fraction: number | null, loaded: number, total: number | null): void;
}

/**
 * Read a response body, reporting as it goes.
 *
 * Falls back to `response.blob()` where the body is not a readable stream —
 * which is every browser this does not support, and also a response the
 * service worker has already buffered. The download still works; it just has
 * nothing to report until it is done.
 */
export async function readWithProgress(response: Response, onProgress?: Progress): Promise<Blob> {
  const declared = Number(response.headers.get('Content-Length'));
  const total = Number.isFinite(declared) && declared > 0 ? declared : null;
  const body = response.body;

  if (!body || !onProgress) {
    onProgress?.(null, 0, total);
    return response.blob();
  }

  const reader = body.getReader();
  const chunks: BlobPart[] = [];
  let loaded = 0;
  // Reporting on every chunk would re-render the toast a thousand times for a
  // large file, to draw the same percentage repeatedly.
  let lastReported = 0;

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (!value) continue;

    chunks.push(value as unknown as BlobPart);
    loaded += value.byteLength;

    const now = Date.now();
    if (now - lastReported >= 120) {
      lastReported = now;
      onProgress(total === null ? null : loaded / total, loaded, total);
    }
  }

  onProgress(total === null ? null : 1, loaded, total);
  return new Blob(chunks, { type: response.headers.get('Content-Type') ?? '' });
}

/**
 * Hand a blob to the browser as a saved file.
 *
 * The object URL is revoked on a timer rather than on the next line. Revoking
 * it immediately after `click()` is a race every browser currently happens to
 * win, and none of them promises to: the download has been started, not
 * finished, and pulling the URL out from under it is how a save silently
 * produces a zero-byte file.
 */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** The server percent-encodes the name, so it comes back decoded. */
export function filenameFrom(header: string | null, fallback: string): string {
  const match = header ? /filename="([^"]+)"/.exec(header) : null;
  if (!match?.[1]) return fallback;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

/**
 * Fetch, report, save — the whole of a download in one call.
 *
 * `filename` is what to call it when the response does not say; a
 * Content-Disposition from the server wins, because the server is the one
 * that knows a single-folder archive should be called after the folder.
 */
export async function downloadTo(
  url: string,
  filename: string,
  options: { method?: 'GET' | 'POST'; body?: unknown; onProgress?: Progress } = {},
): Promise<void> {
  const response = await authedFetch(url, {
    method: options.method ?? 'GET',
    credentials: 'include',
    ...(options.body === undefined
      ? {}
      : {
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(options.body),
        }),
  });

  if (!response.ok) {
    const detail = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new Error(detail?.message ?? 'That file could not be downloaded.');
  }

  const blob = await readWithProgress(response, options.onProgress);
  saveBlob(blob, filenameFrom(response.headers.get('Content-Disposition'), filename));
}
